import React from 'react'

/**
 * The visual trait that actually differentiates a Classic room at a glance
 * — most of the 14 legacy shells are the same corner box with a different
 * wall material or one architectural feature layered in.
 */
export type ClassicMotif =
  | 'drape'
  | 'flute'
  | 'plank'
  | 'corrugated'
  | 'colorblock'
  | 'plain'
  | 'marquee'
  | 'felt'
  | 'beam'
  | 'glossyFloor'
  | 'archway'
  | 'columns'
  | 'concrete'

export interface ClassicGlyphProps {
  wallColor: string
  wallColor2?: string
  floorColor: string
  motif: ClassicMotif
  motifColor?: string
  /** Fixed pixel square — omit to have the glyph fill its parent box edge-to-edge instead (the picker card use case). */
  size?: number
  className?: string
}

function shade(hex: string, amt: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!m) return hex
  const clamp = (v: number) => Math.max(0, Math.min(255, v))
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => clamp(parseInt(h, 16) + amt))
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

// Wide 200×100 stage — matches a card cover's rectangular aspect (not a
// square badge floating in one), so the glyph reads as a cover, not an icon.
// Bands: ceiling 0–16, wall 16–72, floor 72–100.

/** Renders the motif-specific linework on top of the base room shell. Kept in one switch so each glyph stays a handful of primitives. */
function Motif({ motif, wallColor, wallColor2, motifColor }: Required<Pick<ClassicGlyphProps, 'motif' | 'wallColor'>> & Pick<ClassicGlyphProps, 'wallColor2' | 'motifColor'>) {
  const accent = motifColor || shade(wallColor, -40)
  switch (motif) {
    case 'drape':
      return (
        <g opacity={0.9}>
          <polygon
            points='0,26 50,18 100,28 150,20 200,26 200,66 150,58 100,68 50,60 0,66'
            fill={wallColor}
          />
          <polyline points='0,26 50,18 100,28 150,20 200,26' fill='none' stroke={shade(wallColor, 30)} strokeWidth={2} />
          <polyline points='0,66 50,60 100,68 150,58 200,66' fill='none' stroke={shade(wallColor, -35)} strokeWidth={2} />
        </g>
      )
    case 'flute':
      return (
        <g stroke={shade(wallColor, -22)} strokeWidth={2} opacity={0.55}>
          {[20, 45, 70, 95, 120, 145, 170, 195].map((x) => (
            <line key={x} x1={x} y1={20} x2={x} y2={68} />
          ))}
        </g>
      )
    case 'plank':
      return (
        <g stroke={shade(wallColor, -30)} strokeWidth={1.5} opacity={0.5}>
          {[30, 42, 54, 66].map((y) => (
            <line key={y} x1={0} y1={y} x2={200} y2={y} />
          ))}
        </g>
      )
    case 'corrugated':
      return (
        <g fill='none' stroke={shade(wallColor, 40)} strokeWidth={2} opacity={0.7}>
          <path d='M0,20 Q12,12 24,20 T48,20 T72,20 T96,20 T120,20 T144,20 T168,20 T192,20' />
        </g>
      )
    case 'colorblock':
      return <rect x={100} y={16} width={100} height={56} fill={wallColor2 || accent} />
    case 'marquee':
      return (
        <g>
          <line x1={50} y1={4} x2={50} y2={24} stroke={accent} strokeWidth={2} />
          <line x1={100} y1={2} x2={100} y2={24} stroke={accent} strokeWidth={2} />
          <line x1={150} y1={4} x2={150} y2={24} stroke={accent} strokeWidth={2} />
          <rect x={35} y={24} width={130} height={26} fill='#f4f3f6' />
        </g>
      )
    case 'felt':
      return (
        <g fill={shade(wallColor, 22)} opacity={0.35}>
          {[30, 70, 110, 150, 190].flatMap((x) => [28, 44, 60].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r={1.8} />))}
        </g>
      )
    case 'beam':
      return (
        <g fill={accent}>
          <rect x={60} y={14} width={16} height={24} rx={1} />
          <rect x={124} y={14} width={16} height={24} rx={1} />
        </g>
      )
    case 'glossyFloor':
      return (
        <g>
          <polygon points='55,76 145,76 128,98 72,98' fill='#ffffff' opacity={0.3} />
          <polygon points='80,76 120,76 112,88 88,88' fill='#ffffff' opacity={0.22} />
        </g>
      )
    case 'archway':
      return (
        <g>
          <rect x={0} y={16} width={46} height={56} fill={wallColor2 || accent} />
          <rect x={154} y={16} width={46} height={56} fill={wallColor2 || accent} />
          <path d='M70,72 L70,38 Q70,22 100,22 Q130,22 130,38 L130,72 Z' fill='#f4f3f6' />
        </g>
      )
    case 'columns':
      return (
        <g fill={shade(wallColor, 34)} opacity={0.85}>
          <rect x={46} y={16} width={14} height={56} />
          <rect x={140} y={16} width={14} height={56} />
        </g>
      )
    case 'concrete':
      return (
        <g stroke={shade(wallColor, -18)} strokeWidth={1} opacity={0.5}>
          <line x1={0} y1={34} x2={200} y2={34} />
          <line x1={0} y1={54} x2={200} y2={54} />
          <line x1={66} y1={16} x2={66} y2={72} />
          <line x1={134} y1={16} x2={134} y2={72} />
        </g>
      )
    case 'plain':
    default:
      return null
  }
}

/**
 * Stylized "room corner" cover standing in for a Classic scene screenshot.
 * Legacy Babylon screenshots read as noise at the size the Studio picker
 * actually renders them — this trades photographic fidelity for a flat,
 * on-brand glyph built from each room's real wall/floor palette plus one
 * linework motif for its single distinguishing feature (a beam, a
 * color-blocked wall, a hanging marquee, etc). Fills its parent box
 * edge-to-edge like a real cover, rather than sitting as a small icon
 * centered in a larger card.
 */
export function ClassicGlyph({ wallColor, wallColor2, floorColor, motif, motifColor, size, className }: ClassicGlyphProps) {
  const ceil = shade(wallColor, 26)
  return (
    <svg
      width={size ?? '100%'}
      height={size ?? '100%'}
      viewBox='0 0 200 100'
      preserveAspectRatio='none'
      className={className}
      role='img'
      aria-hidden='true'
      style={{ display: 'block' }}
    >
      <rect x={0} y={0} width={200} height={16} fill={ceil} />
      <rect x={0} y={16} width={200} height={56} fill={wallColor} />
      <rect x={0} y={72} width={200} height={28} fill={floorColor} />
      <rect x={0} y={72} width={200} height={8} fill='#000000' opacity={0.16} />
      <Motif motif={motif} wallColor={wallColor} wallColor2={wallColor2} motifColor={motifColor} />
      <rect x={0} y={0} width={200} height={100} fill='#000000' opacity={0.06} />
    </svg>
  )
}
